/* eslint-disable import/no-cycle */
/* eslint-disable max-lines */
import {
  useCallback,
  useEffect,
  useState,
  useContext,
  useMemo,
  useRef
} from 'react';
import Button from '@base/Button/Button';
import H4 from '@base/H4/H4';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import { Context as PurchaseTypeContext } from '@context/PurchaseTypeContext';
import usePaymentMethods from '@hooks/usePaymentMethods';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import { readCookie } from '@utils/cookies';
import { businessPlatform } from '@utils/constants';
import pushRoute from '@utils/pushRoute';
import acpfNudgeHelper from '@utils/acpfNudgeHelper';
import BottomSheet from '@comp/BottomSheet/BottomSheet';
import getQueryString from '@utils/checkout';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { breakpoints, Spacings } from '@styles/index';
import {
  subscriptionSubTypes,
  subscriptionTypes,
  subscriptionSubTypesObj,
  isCentreSubscription
} from '@constants/index';
import { segment } from '@actions/analytics';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import {
  getIsLoggedIn,
  getJWTToken,
  getMe,
  getPlatformUnlockStatus
} from '@api/selectors/auth';
import useOfflineCentresStore from '@stores/offline-centres';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import getItemFromSessionStorage from '@utils/getItemFromSessionStorage';
import { emptyObject } from '@constants/empty';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import isEmpty from 'lodash/isEmpty';
import useStates from '@api/hooks/useStates';
import Dynamic from '@base/Dynamic/Dynamic';
import {
  getIsSubscribedToGoal,
  getIsSubscribedToIconic
} from '@api/selectors/subscription';
import {
  enrollJuspayInstalmentOrder,
  enrollJuspayOrder
} from '@api/fetchers/payment';
import { getParameterByName } from '@utils/textHelper';
import getPreservedQueryParams from '@utils/getPreservedQueryParams';
import { useIsMobile } from '@stores/AppCommonData';
import { localStorageAvailable } from '@utils/localStorage';
import loadScript from '@utils/loadScript';
import useMounted from '@hooks/useMounted';
import Login from '@cont/Login/Login';
import Typography from 'aquilla/core/atoms/Typography';
import KeyboardArrowDown from '@icon/KeyboardArrowDown';
import { isWebView } from '@utils/deviceHelper';
import { setSelectedGoal } from '@actions/auth';
import handleAppPaymentRedirection from '@utils/handleAppPaymentRedirection';
import {
  CHECKOUT_STARTED,
  CHECKOUT_CHANGE_DURATION,
  CHECKOUT_RECEIPT_GENERATED,
  CHECKOUT_PAYMENT_PAGE_LOGGEDIN,
  REFER_LINK_CLICKED,
  CHECKOUT_TRIAL_PAYMENT_PAGE_LOGGEDIN,
  CHECKOUT_PRIME_PAYMENT_PAGE_LOGGEDIN
} from './constants/segment';
import PaymentMethods from './PaymentMethods';
import SubscriptionInfo from './SubscriptionInfo';
import UserInfo from './UserInfo';
import {
  paymentGatewayTypes,
  PAYMENT_TYPES,
  STORESUBSCRIPTION_PROCESSOR,
  STOREBUNDLE_PROCESSOR,
  isAppRedirectedKey,
  UPI_Intent,
  UPI_QR
} from './constants';
import { getPaymentMethod, orderCreatedAnalytics } from './utils';
import { getTotalAmtBySubscription } from './usePurchaseDetails';
import useReferralCodeSuggestionsExp from './useReferralCodeSuggestionsExp';
import CancelPaymentComponent from './CashPayment/CancelPaymentComponent';
import CheckoutStateSelect from './PreCheckout/MwebPlanSelectionRevamp/CheckoutStateSelect';
import TrialPaymentFailedDialog from './TrialPaymentFailedDialog';

const PayInPartsStatus = Dynamic(() => import('./PayInParts/PayInPartsStatus'));
const PaymentFailedDialog = Dynamic(() => import('./PaymentFailedDialog'));
const UPIPaymentFailedDialog = Dynamic(
  () => import('./UPIPaymentFailedDialog')
);
const PartPaymentSuccessDialog = Dynamic(
  () => import('./PayInParts/PartPaymentSuccessDialog')
);
// eslint-disable-next-line import/no-cycle
const UPILoader = Dynamic(() => import('./UPILoader'));

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
  background-color: var(--color-i-white);
  justify-content: center;
`;

const MobileWrapper = styled.div`
  @media only screen and (max-width: ${breakpoints.tablet}) {
    grid-column: 1/15;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: flex;
    grid-column: 1/9;
    flex-direction: column-reverse;
  }
`;

const StyBottomSheet = styled(BottomSheet)``;

const LeftWrapper = styled.div<{ $partPaymentActive: boolean }>`
  grid-column: 1/17;
  margin-top: ${Spacings.SPACING_2B};
  display: flex;
  flex-direction: column;
  margin-bottom: ${Spacings.SPACING_30B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    grid-column: 1/15;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: 1/9;
    box-shadow: 0px 16px 32px 0px rgba(221, 230, 237, 0.4);
    margin-bottom: ${Spacings.SPACING_0B};
    margin-top: ${Spacings.SPACING_0B};
    flex-direction: ${({ $partPaymentActive }) =>
      $partPaymentActive ? 'column' : 'column-reverse'};
  }
`;

const RightWrapper = styled.div`
  grid-column: 17/25;
  margin-top: ${Spacings.SPACING_2B};
  position: relative;
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    grid-column: 15/25;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: 1/9;
    margin-top: 0px;
  }
`;

const DetailsContainer = styled.div`
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-base-1);
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_3B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    padding: ${Spacings.SPACING_6B};
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    padding: 16px;
    margin-bottom: 0px;
  }
`;

const PaymentMethodsDisabledContainer = styled(DetailsContainer)`
  border: 1px solid var(--color-divider);
`;

const ProgressWrapper = styled.div`
  height: 372px;
  margin: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
`;

const PageLoader = styled.section`
  grid-column: 1/25;
  height: calc(100vh - 140px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CheckoutStateSelectWrapper = styled.div`
  margin-top: -${Spacings.SPACING_4B};
  padding: ${Spacings.SPACING_4B};
  grid-column: span 24;
  background-color: var(--color-base-1);
`;

const WebViewHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: var(--color-base-1);
  padding: 16px;
  margin-bottom: 8px;
  gap: 24px;
`;

const StyArrowBack = styled(KeyboardArrowDown)`
  transform: rotate(90deg);
`;

const referrerInfo = getItemFromSessionStorage('referrer_info');

const PaymentFlow = () => {
  const router = useRouter();
  const { pathname, asPath } = router;
  const {
    goalUID,
    subscriptionUID,
    referral_code = '',
    payment_status,
    part_payment_status,
    gUid: gUID,
    goalSlug,
    source = null
  } = router.query;
  const isAppRedirected = source === isAppRedirectedKey;

  const headers = isAppRedirected
    ? {
        'x-platform-source': 1
      }
    : {};

  const {
    state: {
      subscription,
      subscriptionWithoutDiscount,
      isCombo,
      creditsApplied,
      partPaymentActive,
      selectedPayInPartsPlan,
      selectedMethod,
      availablePaymentMethods,
      setLoanInterestAmt,
      referralError,
      isLoading,
      isError,
      validateReferralInProgress,
      setOrder,
      setIsCreatingOrder,
      arePaymentMethodsLoading,
      areStatusCallsDone,
      pendingPaymentType,
      partPaymentStatus,
      cashResponse,
      mwebCheckoutV2,
      upiApp,
      setPaymentPossible,
      paymentPossible
    }
  } = usePaymentMethods();

  const purchaseType = useContext(PurchaseTypeContext);
  const isStoreBundle = subscription?.processorName === STOREBUNDLE_PROCESSOR;
  const isStoreSubscriptionBased =
    subscription?.processorName === STORESUBSCRIPTION_PROCESSOR;
  useEffect(() => {
    if (isLoading || isError) return;

    const isPlatform = subscription.type === subscriptionTypes.PLATFORM;
    if (
      isPlatform &&
      pathname !== '/batch-group/[slug]/subscribe/[subscriptionUID]'
    ) {
      pushRoute(
        `/batch-group/[slug]/subscribe/[subscriptionUID]${getPreservedQueryParams()}`,
        `/batch-group/-/subscribe/${subscriptionUID}${getPreservedQueryParams()}`,
        {},
        '',
        true
      );
    }
    if (
      !isPlatform &&
      pathname !== '/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]'
    ) {
      pushRoute(
        `/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]${getPreservedQueryParams()}`,
        `/goal/-/${
          subscription.goal.uid
        }/subscribe/${subscriptionUID}${getPreservedQueryParams()}`,
        {},
        '',
        true
      );
    }
  }, [subscription, pathname, isError, isLoading, subscriptionUID]);
  const userLoggedIn = useMyInfo(getIsLoggedIn);
  const isLoggedIn = !!readCookie('accessToken') && userLoggedIn;
  const isMounted = useMounted();
  const meJwt = useMyInfo(getJWTToken);
  const [cashPaymentStatus, setCashPaymentStatus] = useState(null);

  useEffect(() => {
    setCashPaymentStatus(cashResponse);
  }, [cashResponse]);
  const { cityUID: offlineCentreCity } = useOfflineCentresStore.getState();
  const isOfflineSubscription = subscription
    ? subscription.type === subscriptionTypes.CENTRE ||
      isCentreSubscription(subscription?.type, subscription?.contentTypeId)
    : false;

  const { states } = useStates();
  const { refetchMyInfo } = useMyInfo();
  const me = useMyInfo(getMe) || emptyObject;
  const { state, phone = '' } = me;

  const [isStateSelected, setIsStateSelected] = useState(true);

  const [paymentStatus, setPaymentStatus] = useState(payment_status);
  const [showPartPaymentSuccessDialog, setShowPartPaymentSuccessDialog] =
    useState(part_payment_status === 'success');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    if (!state && isLoggedIn && mwebCheckoutV2) setIsStateSelected(false);
  }, [state, isLoggedIn, mwebCheckoutV2]);

  useFirstRenderSkip(() => {
    if (isStateSelected) refetchMyInfo();
  }, [isStateSelected, refetchMyInfo]);

  useEffect(() => {
    if (part_payment_status === 'success')
      setShowPartPaymentSuccessDialog(true);
  }, [part_payment_status]);

  const closePaymentFailedDialog = () => {
    setPaymentStatus(null);
  };

  const closeUPIPaymentFailedDialog = () => {
    setPaymentPossible(true);
  };

  const handlePayLater = () => {
    const goalToSelect = isCombo
      ? subscription.childSubscriptions[0].goal.uid
      : goalUID;

    setSelectedGoal(goalToSelect);
    const isRedirected = handleAppPaymentRedirection(
      router.query,
      'home',
      null,
      goalToSelect
    );
    if (isRedirected) return;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) window.open(`/goal/-/${goalToSelect}`, '_self');
    pushRoute('/goal/[goalSlug]/[goalUID]', `/goal/-/${goalToSelect}`);
  };

  const closePartPaymentSuccessDialog = () => {
    setShowPartPaymentSuccessDialog(false);
  };

  const isExtendPlan = useMyInfo(getIsSubscribedToGoal(goalUID));
  const isSubscribedToIconic = useMyInfo(getIsSubscribedToIconic(goalUID));

  const isIconicUpgrade = isExtendPlan && !isSubscribedToIconic;
  const isUpgradeOrExtend = isExtendPlan || isIconicUpgrade;

  useEffect(() => {
    if (
      !isUpgradeOrExtend &&
      !isLoading &&
      !isError &&
      !isStoreSubscriptionBased
    ) {
      if (!isCombo) {
        acpfNudgeHelper(
          subscription.goal.name,
          '-',
          goalUID,
          subscriptionUID,
          isCombo,
          paymentStatus === 'failed' ? 2 : 1
        );
      } else {
        subscription.childSubscriptions.forEach(({ goal }) => {
          acpfNudgeHelper(
            goal.name,
            '-',
            goal.uid,
            subscriptionUID,
            isCombo,
            paymentStatus === 'failed' ? 2 : 1,
            goalUID
          );
        });
      }
    }
  }, [
    isLoading,
    isError,
    isUpgradeOrExtend,
    isCombo,
    paymentStatus,
    goalUID,
    subscriptionUID,
    subscription,
    isStoreSubscriptionBased
  ]);

  const eventProps = useCheckoutEventProps(
    goalUID,
    subscription,
    isCombo,
    creditsApplied
  );

  const updateUrl = useCallback(
    ({ referralCode, credsApplied }) => {
      const queryParams = getQueryString({
        referralCode: encodeURIComponent(referralCode || ''),
        credsApplied,
        gUid: gUID
      });
      const asPathWithoutQueryParams = asPath.split('?')[0];
      pushRoute(
        `${pathname}${queryParams}`,
        `${asPathWithoutQueryParams}${queryParams}`,
        { shallow: true }
      );
    },
    [pathname, asPath, gUID]
  );

  const applyReferral = useCallback(
    (referralCode) => {
      updateUrl({ referralCode, credsApplied: creditsApplied });
    },
    [creditsApplied, updateUrl]
  );

  const resetReferral = useCallback(() => {
    updateUrl({ referralCode: '', credsApplied: creditsApplied });
  }, [creditsApplied, updateUrl]);

  const applyCredits = useCallback(() => {
    updateUrl({ referralCode: referral_code, credsApplied: true });
  }, [referral_code, updateUrl]);

  const removeCredits = useCallback(() => {
    updateUrl({ referralCode: referral_code, credsApplied: false });
  }, [referral_code, updateUrl]);

  const getReferralData = useCallback(() => {
    if (referrerInfo) {
      const referrerData = JSON.parse(referrerInfo);
      const { referral_type = 'NA' } = referrerData;
      const referralType =
        referrerData.referrer_code === referral_code ? referral_type : 'NA';
      sessionStorage.removeItem('referrer_info');

      return { ...referrerData, referral_type: referralType };
    }
    return null;
  }, [referral_code]);

  const sendReferralLinkEvent = useCallback(() => {
    const referrerData = getReferralData();
    if (referrerData) {
      segment.track(REFER_LINK_CLICKED, {
        ...eventProps,
        ...referrerData,
        referrer_code: referral_code
      });
    }
  }, [getReferralData, eventProps, referral_code]);

  const sendCheckoutStartedEvent = useCallback(() => {
    const referrerData = getReferralData();
    if (referrerData) {
      let notesSegmentData = {};
      if (isStoreBundle) {
        if (localStorageAvailable()) {
          notesSegmentData = JSON.parse(
            localStorage.getItem('notesSegmentData')
          );
        }
      }
      if (isStoreBundle) {
        segment.track(CHECKOUT_STARTED, {
          ...eventProps,
          ...notesSegmentData,
          entity_content_type: 'Notes Combo',
          purchase_type: purchaseType,
          referral_type: referrerData.referral_type
        });
      } else {
        segment.track(CHECKOUT_STARTED, {
          ...eventProps,
          purchase_type: purchaseType,
          referral_type: referrerData.referral_type
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getReferralData, eventProps, purchaseType]);

  const platformUnlockStatus = useMyInfo(getPlatformUnlockStatus);

  const initialReferral = useRef(
    getParameterByName('referral_code', asPath) || ''
  );

  useEffect(() => {
    if (
      !isEmpty(platformUnlockStatus) &&
      !initialReferral.current &&
      isLoggedIn &&
      areStatusCallsDone &&
      !pendingPaymentType
    ) {
      const { isPlatformUnlocked } = platformUnlockStatus;
      const platformUnlockedRefCode = platformUnlockStatus.referralCode;

      if (isPlatformUnlocked && platformUnlockedRefCode) {
        applyReferral(platformUnlockedRefCode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    platformUnlockStatus,
    isLoggedIn,
    areStatusCallsDone,
    pendingPaymentType
  ]);

  useEffect(() => {
    if (isLoggedIn && subscription) {
      const isPrimeSubscription = subscription?.contentTypeId === 507;
      segment.track(
        // eslint-disable-next-line no-nested-ternary
        subscription?.type === subscriptionTypes.TRIAL
          ? CHECKOUT_TRIAL_PAYMENT_PAGE_LOGGEDIN
          : isPrimeSubscription
          ? CHECKOUT_PRIME_PAYMENT_PAGE_LOGGEDIN
          : CHECKOUT_PAYMENT_PAGE_LOGGEDIN,
        {
          ...eventProps,
          business_platform: isOfflineSubscription
            ? businessPlatform.UA_OFFLINE
            : businessPlatform.UNACADEMY,
          batch_group_name: subscription?.title,
          entity_content_type: isPrimeSubscription
            ? 'Prime'
            : eventProps?.entity_content_type
        }
      );
      sendReferralLinkEvent();
      sendCheckoutStartedEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, isOfflineSubscription]);

  useFirstRenderSkip(() => {
    if (isLoggedIn) {
      const referrerInfo = getItemFromSessionStorage('referrer_info');
      if (referrerInfo) {
        const referrerData = JSON.parse(referrerInfo);
        segment.track(REFER_LINK_CLICKED, {
          ...eventProps,
          ...referrerData,
          referrer_code: referral_code
        });
        sessionStorage.removeItem('referrer_info');
      }
    }
  }, [isLoggedIn, eventProps, referral_code]);

  useEffect(() => {
    if (isLoggedIn && paymentPossible) {
      loadScript(process.env.JUSPAY_URL, 'juspay');
    }
  }, [isLoggedIn, paymentPossible]);

  const changeDuration = useCallback(() => {
    segment.track(CHECKOUT_CHANGE_DURATION, eventProps);

    if (isOfflineSubscription) {
      if (offlineCentreCity) {
        pushRoute(
          `/offline/centres/subscribe/[id]${getPreservedQueryParams({
            uid: subscription.uid,
            goal: goalUID
          })}`,
          `/offline/centres/subscribe/${offlineCentreCity}${getPreservedQueryParams(
            {
              uid: subscription.uid,
              goal: goalUID
            }
          )}`
        );
      } else {
        pushRoute(
          `/offline/centres/subscribe${getPreservedQueryParams({
            uid: subscription.uid,
            goal: goalUID
          })}`,
          `/offline/centres/subscribe${getPreservedQueryParams({
            uid: subscription.uid,
            goal: goalUID
          })}`
        );
      }
      return;
    }

    let goal = goalUID;

    if (
      gUID &&
      subscription.childSubscriptions.find(({ goal: { uid } }) => uid === gUID)
    ) {
      goal = gUID;
    }
    const queryParams = {
      uid: subscription.uid,
      // @ts-ignore
      referral_code: encodeURIComponent(referral_code || ''),
      is_iconic: subscription.type === subscriptionTypes.ICONIC,
      show_pricing: true
    };
    pushRoute(
      `/goal/[goalSlug]/[goalUID]/subscribe${getPreservedQueryParams({
        ...queryParams
      })}`,
      `/goal/${goalSlug}/${goal}/subscribe${getPreservedQueryParams({
        ...queryParams
      })}`
    );
  }, [
    eventProps,
    isOfflineSubscription,
    subscription,
    goalUID,
    goalSlug,
    referral_code,
    gUID,
    offlineCentreCity
  ]);

  const { selectedGoal } = useSelectedGoal();
  const isMobile = useIsMobile();

  const queryParams = router.asPath?.split('?') ?? [];
  const paramsForReturnUrl = queryParams[1] ? `?${queryParams[1]}` : '';

  const referralInputRef = useRef();

  useEffect(() => {
    // @ts-ignore
    if (referralInputRef.current)
      // @ts-ignore
      referralInputRef.current.value = referral_code;
  }, [referral_code]);

  const extraDuration = useMemo(() => {
    if (partPaymentStatus?.isActivePartSet)
      return partPaymentStatus.offerDetails?.offeredDurationDays;
    if (cashPaymentStatus?.subscriptionDetails)
      return cashPaymentStatus.offerDetails?.offeredDurationDays;
    if (subscription?.availableOffer?.offeredDurationDays)
      return subscription?.availableOffer?.offeredDurationDays;
    return 0;
  }, [partPaymentStatus, cashPaymentStatus, subscription]);

  const { isSuggestionFlow } = useReferralCodeSuggestionsExp();

  const createOrderForSubscription = (
    paymentMethod,
    paymentMethodType,
    phoneNumber,
    emiData,
    gateway = 2,
    cashBank,
    cardType,
    loanBank
  ) => {
    const referral = referralError ? null : referral_code;
    const upi_app_key = 'com.phonepe.app';
    setIsCreatingOrder(true);
    if (upiApp.length > 0) {
      return enrollJuspayOrder(
        subscriptionUID,
        {
          renew: false,
          referral,
          phone: phoneNumber || phone,
          scheme: 2,
          use_credits: creditsApplied,
          payment_method: paymentMethod,
          payment_method_type: paymentMethodType,
          emi_data: emiData,
          gateway,
          upgrade: true,
          is_partial: partPaymentActive,
          no_of_installments: 0,
          params_for_return_url: paramsForReturnUrl,
          return_url: process.env.PAYMENT_RETURN_URL,
          bank_name: cashBank,
          txn_type: 'UPI_PAY',
          upi_app: upi_app_key
        },
        headers
      )
        .then((data) => {
          if (data?.orderId) {
            setOrder(data);
            orderCreatedAnalytics({
              subscriptionId: subscriptionUID,
              creditsApplied,
              phoneNumber: phoneNumber || phone,
              referralCode: referral,
              paymentMethod: UPI_Intent,
              paymentMethodType,
              emiData,
              isPartial: true,
              subscription,
              me,
              selectedGoal,
              data,
              paymentDetails: partPaymentStatus?.partPayments,
              isCombo,
              comboId: subscription.goal?.uid,
              comboName: subscription.goal?.name,
              referralError,
              selectedMethod,
              purchaseType,
              cardType
            });
          }
          return data;
        })
        .finally(() => setIsCreatingOrder(false));
    }

    if (partPaymentStatus?.isActivePartSet) {
      const pipIndex =
        partPaymentStatus.partPayments?.findIndex((item) => !item.payment) + 1;

      const orderUid = partPaymentStatus.partPayments.find(
        (item) => !item.payment
      )?.uid;
      const parts = partPaymentStatus?.partPayments?.length;

      enrollJuspayInstalmentOrder(
        subscriptionUID,
        orderUid,
        {
          renew: false,
          referral,
          phoneNumber: phoneNumber || phone,
          scheme: 2,
          use_credits: creditsApplied,
          payment_method: paymentMethod === UPI_QR ? 'UPI' : paymentMethod,
          payment_method_type:
            paymentMethod === UPI_QR ? 'UPI' : paymentMethodType,
          emi_data: emiData,
          gateway,
          upgrade: true,
          params_for_return_url: paramsForReturnUrl,
          return_url: process.env.PAYMENT_RETURN_URL
        },
        headers
      )
        .then((data) => {
          if (data?.orderId) {
            setOrder(data);
            orderCreatedAnalytics({
              subscriptionId: subscriptionUID,
              creditsApplied,
              phoneNumber: phoneNumber || phone,
              referralCode: referral,
              paymentMethod,
              paymentMethodType,
              emiData,
              isPartial: true,
              parts,
              subscription,
              me,
              selectedGoal,
              data,
              pipIndex,
              paymentDetails: partPaymentStatus?.partPayments,
              isCombo,
              comboId: subscription.goal?.uid,
              comboName: subscription.goal?.name,
              referralError,
              selectedMethod,
              purchaseType,
              cardType
            });
          }
        })
        .finally(() => setIsCreatingOrder(false));
    } else {
      const parts = partPaymentActive ? selectedPayInPartsPlan?.length : 0;
      enrollJuspayOrder(
        subscriptionUID,
        {
          renew: false,
          referral,
          phone: phoneNumber || phone,
          scheme: 2,
          use_credits: creditsApplied,
          payment_method: paymentMethod === UPI_QR ? 'UPI' : paymentMethod,
          payment_method_type:
            paymentMethod === UPI_QR ? 'UPI' : paymentMethodType,
          emi_data: emiData,
          gateway,
          upgrade: true,
          is_partial: partPaymentActive,
          no_of_installments: parts,
          params_for_return_url: paramsForReturnUrl,
          return_url: process.env.PAYMENT_RETURN_URL,
          bank_name: cashBank,
          txn_type: paymentMethod === UPI_QR ? paymentMethod : null
        },
        headers
      )
        .then((data) => {
          if (gateway === 8) {
            let status = false;
            if (data) {
              status = true;
              const isRedirected = handleAppPaymentRedirection(
                router.query,
                'cash',
                null,
                goalUID
              );
              if (isRedirected) return;
              setCashPaymentStatus(data);
            }

            segment.track(CHECKOUT_RECEIPT_GENERATED, {
              ...eventProps,
              order_id: data?.orderId || '',
              has_credits_applied: creditsApplied,
              credits_applied: creditsApplied
                ? subscription.redeemableCreditAmount
                : 0,
              receipt_generate_status: status,
              subscription_price: getTotalAmtBySubscription(subscription),
              payment_gateway: paymentGatewayTypes[8],
              payment_method: 'Cash'
            });
          }

          if (data?.orderId) {
            setOrder(data);

            orderCreatedAnalytics({
              subscriptionId: subscriptionUID,
              creditsApplied,
              phoneNumber,
              referralCode: referral,
              paymentMethod,
              paymentMethodType,
              emiData,
              isPartial: partPaymentActive,
              parts,
              subscription,
              me,
              selectedGoal,
              data,
              pipIndex: 1,
              paymentDetails: selectedPayInPartsPlan,
              isCombo,
              comboId: subscription.goal?.uid,
              comboName: subscription.goal?.name,
              referralError,
              selectedMethod,
              purchaseType,
              gateway,
              cardType,
              loanBank
            });
          }
        })
        .finally(() => setIsCreatingOrder(false));
    }
  };

  const handleEbanxPayment = () => {
    setIsCreatingOrder(true);
    enrollJuspayOrder(
      subscriptionUID,
      {
        renew: false,
        referral: referral_code || null,
        phone,
        scheme: 2,
        use_credits: creditsApplied,
        payment_method: null,
        payment_method_type: null,
        emi_data: null,
        gateway: 5,
        upgrade: true,
        is_partial: partPaymentActive,
        no_of_installments: partPaymentActive
          ? selectedPayInPartsPlan.length
          : 0,
        params_for_return_url: paramsForReturnUrl,
        return_url: process.env.PAYMENT_RETURN_URL,
        bank_name: null
      },
      headers
    )
      .then((data) => {
        if (data) {
          window.location = data?.paymentLinks?.iframe;
        }
      })
      .finally(() => setIsCreatingOrder(false));
  };

  const discountedSubscription = useMemo(() => {
    return validateReferralInProgress
      ? null
      : {
          subscription,
          referral: referralError
            ? { message: referralError }
            : subscription?.referral
        };
  }, [validateReferralInProgress, subscription, referralError]);

  if (isLoading || isError) {
    return (
      <PageLoader>
        <CircularLoader />
      </PageLoader>
    );
  }

  const handleBackClick = () => {
    window.open(
      `/goal/-/${subscription.goal.uid}/subscribe?close_web_view=true&navigation_reset=true`,
      '_blank'
    );
  };
  const WebViewHeader = () => {
    if (mwebCheckoutV2 && isWebView({}) && !selectedMethod)
      return (
        <WebViewHeaderWrapper onClick={() => setIsDrawerOpen(true)}>
          <StyArrowBack size="24px" color="var(--color-text-primary)" />
          <Typography variant="h5">Select payment method</Typography>
        </WebViewHeaderWrapper>
      );
    return null;
  };

  const priceSummaryDrawerProps = {
    isReferralPreapplied: !!referral_code,
    referralInputRef,
    changeDuration,
    onApply: applyReferral,
    resetReferral,
    applyCredits,
    removeCredits,
    isCombo,
    subscription: subscriptionWithoutDiscount,
    discountedSubscription,
    referral: referral_code,
    creditsApplied,
    partPaymentActive,
    partPaymentStatus,
    cashResponse: cashPaymentStatus,
    setLoanInterestAmt,
    isLoanSelected: selectedMethod === PAYMENT_TYPES.LOAN,
    loanMeta:
      getPaymentMethod(availablePaymentMethods, PAYMENT_TYPES.LOAN)?.metaInfo ||
      emptyObject,
    bundleDetails: isCombo
      ? {
          topologies: subscription.childSubscriptions.map(({ type, goal }) => ({
            ...goal,
            type
          }))
        }
      : null,
    goal: subscription.goal,
    referralError: { message: referralError },
    me,
    extraDuration,
    isSuggestionFlow,
    validateReferralInProgress
  };

  const getPaymentMethods = () => {
    if (availablePaymentMethods.length && !arePaymentMethodsLoading) {
      // @ts-ignore
      return (
        <PaymentMethods
          createOrder={createOrderForSubscription}
          cashPaymentStatus={cashPaymentStatus}
          partPaymentStatus={partPaymentStatus}
          setCashPaymentStatus={setCashPaymentStatus}
          priceSummaryDrawerProps={priceSummaryDrawerProps}
        />
      );
    }
    return (
      <PaymentMethodsDisabledContainer>
        <H4 as="h2">Choose a payment method</H4>
        <ProgressWrapper>
          <CircularLoader />
        </ProgressWrapper>
      </PaymentMethodsDisabledContainer>
    );
  };

  const renderPaymentMethods = () => {
    if (isLoggedIn) {
      if (goalUID === 'QZIXN') {
        return (
          <Button
            // @ts-ignore
            label="Pay"
            onClick={handleEbanxPayment}
          />
        );
      }
      if (state || (!state && states.length < 1) || mwebCheckoutV2) {
        return getPaymentMethods();
      }
    }
    return null;
  };

  const renderPaymentFlow = () => {
    return (
      <>
        <LeftWrapper $partPaymentActive={partPaymentActive && mwebCheckoutV2}>
          {!mwebCheckoutV2 && <UserInfo />}
          {mwebCheckoutV2 && isMobile && !isLoggedIn && isMounted && (
            <DetailsContainer>
              <Login source="page" />
            </DetailsContainer>
          )}
          {partPaymentActive && (
            <PayInPartsStatus
              // @ts-ignore
              partPaymentStatus={partPaymentStatus}
              renderPaymentMethods={renderPaymentMethods}
              showPartPaymentSuccessDialog={showPartPaymentSuccessDialog}
              setShowPartPaymentSuccessDialog={setShowPartPaymentSuccessDialog}
              priceSummaryDrawerProps={priceSummaryDrawerProps}
            />
          )}
          {mwebCheckoutV2 && partPaymentActive ? null : renderPaymentMethods()}
        </LeftWrapper>
        {(!mwebCheckoutV2 || !isMobile || !selectedMethod) && (
          <RightWrapper>
            <SubscriptionInfo
              isReferralPreapplied={!!referral_code}
              referralInputRef={referralInputRef}
              changeDuration={changeDuration}
              onApply={applyReferral}
              resetReferral={resetReferral}
              applyCredits={applyCredits}
              removeCredits={removeCredits}
              isCombo={isCombo}
              subscription={subscriptionWithoutDiscount}
              discountedSubscription={discountedSubscription}
              // @ts-ignore
              referral={referral_code}
              creditsApplied={creditsApplied}
              partPaymentActive={partPaymentActive}
              partPaymentStatus={partPaymentStatus}
              cashResponse={cashPaymentStatus}
              setLoanInterestAmt={setLoanInterestAmt}
              isLoanSelected={selectedMethod === PAYMENT_TYPES.LOAN}
              loanMeta={
                getPaymentMethod(availablePaymentMethods, PAYMENT_TYPES.LOAN)
                  ?.metaInfo || emptyObject
              }
              bundleDetails={
                isCombo
                  ? {
                      topologies: subscription.childSubscriptions.map(
                        ({ type, goal }) => ({ ...goal, type })
                      )
                    }
                  : null
              }
              goal={subscription.goal}
              referralError={{ message: referralError }}
              me={me}
              extraDuration={extraDuration}
              isSuggestionFlow={isSuggestionFlow}
              validateReferralInProgress={validateReferralInProgress}
              isMandate={availablePaymentMethods?.[0]?.isUpiMandate}
            />
          </RightWrapper>
        )}
        {paymentStatus === 'failed' &&
          subscription?.type !== subscriptionTypes.TRIAL &&
          (mwebCheckoutV2 ? (
            <StyBottomSheet
              open
              onClose={closePaymentFailedDialog}
              icon={<></>}
            >
              <CancelPaymentComponent
                handleLeftButtonClick={handlePayLater}
                handleRightButtonClick={closePaymentFailedDialog}
                title="Payment failed"
                subTitle="We recommend trying again selecting a different payment method"
                imgSrc="payments/mweb-checkout-v2/payment-method/payment-failure.svg"
                imgAlt="Payment Failed"
                leftButtonCta="Try later"
                leftButonType="ghost"
                rightButtonCta="Try again"
                rightButtonType="primary"
              />
            </StyBottomSheet>
          ) : (
            <PaymentFailedDialog
              // @ts-ignore
              onClose={closePaymentFailedDialog}
              showDialog
            />
          ))}
        {paymentStatus === 'failed' &&
          subscription?.type === subscriptionTypes.TRIAL && (
            <TrialPaymentFailedDialog
              onClose={closePaymentFailedDialog}
              showDialog
              redirectToHome={() => {
                closePaymentFailedDialog();
                const isRedirected = handleAppPaymentRedirection(
                  router.query,
                  'home',
                  null,
                  goalUID
                );
                if (isRedirected) return;
                const isMobile = /iPhone|iPad|iPod|Android/i.test(
                  navigator.userAgent
                );
                if (isMobile) window.open(`/goal/-/${goalUID}`, '_self');
                pushRoute('/goal/[goalSlug]/[goalUID]', `/goal/-/${goalUID}`);
              }}
              url={subscription?.paymentFailureVideoUrl}
            />
          )}
        {!paymentPossible && (
          <div style={{ position: 'relative', bottom: '-20px' }}>
            <UPIPaymentFailedDialog
              // @ts-ignore
              onClose={closeUPIPaymentFailedDialog}
              showDialog
            />
          </div>
        )}
        {showPartPaymentSuccessDialog && !mwebCheckoutV2 && (
          <PartPaymentSuccessDialog
            // @ts-ignore
            onClose={closePartPaymentSuccessDialog}
            showDialog
          />
        )}
      </>
    );
  };

  if (isMobile && mwebCheckoutV2)
    return isStateSelected || !isLoggedIn ? (
      <>
        {!paymentPossible ? (
          <MobileContainer>
            <WebViewHeader />

            <>
              {!paymentPossible ? (
                <>
                  <MobileWrapper>{renderPaymentFlow()}</MobileWrapper>
                </>
              ) : (
                <>
                  {upiApp === undefined || upiApp.length === 0 ? (
                    <>
                      <MobileWrapper>{renderPaymentFlow()}</MobileWrapper>
                    </>
                  ) : (
                    <>
                      <UPILoader
                        key={`${upiApp}`}
                        // @ts-ignore
                        onClickPay={createOrderForSubscription}
                      />
                    </>
                  )}
                </>
              )}
            </>
            <StyBottomSheet
              open={isDrawerOpen}
              onClose={() => setIsDrawerOpen(false)}
              icon={<></>}
            >
              <CancelPaymentComponent
                handleLeftButtonClick={handleBackClick}
                handleRightButtonClick={() => setIsDrawerOpen(false)}
                title="Don’t want to buy subscription?"
                subTitle="Some offers and discounts may no longer by available if you cancel payment now"
                imgSrc="payments/mweb-checkout-v2/payment-method/cancel-payment-method.svg"
                imgAlt="Payment method"
                leftButtonCta="Leave for now"
                leftButonType="danger-outline"
                rightButtonCta="Get subscription"
                rightButtonType="primary"
              />
            </StyBottomSheet>
          </MobileContainer>
        ) : (
          <>
            {upiApp === undefined || upiApp.length === 0 ? (
              <>
                <MobileContainer>
                  <WebViewHeader />

                  <>
                    {!paymentPossible ? (
                      <>
                        <MobileWrapper>{renderPaymentFlow()}</MobileWrapper>
                      </>
                    ) : (
                      <>
                        {upiApp === undefined || upiApp.length === 0 ? (
                          <>
                            <MobileWrapper>{renderPaymentFlow()}</MobileWrapper>
                          </>
                        ) : (
                          <>
                            <UPILoader
                              key={`${upiApp}`}
                              // @ts-ignore
                              onClickPay={createOrderForSubscription}
                            />
                          </>
                        )}
                      </>
                    )}
                  </>
                  <StyBottomSheet
                    open={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                    icon={<></>}
                  >
                    <CancelPaymentComponent
                      handleLeftButtonClick={handleBackClick}
                      handleRightButtonClick={() => setIsDrawerOpen(false)}
                      title="Don’t want to buy subscription?"
                      subTitle="Some offers and discounts may no longer by available if you cancel payment now"
                      imgSrc="payments/mweb-checkout-v2/payment-method/cancel-payment-method.svg"
                      imgAlt="Payment method"
                      leftButtonCta="Leave for now"
                      leftButonType="danger-outline"
                      rightButtonCta="Get subscription"
                      rightButtonType="primary"
                    />
                  </StyBottomSheet>
                </MobileContainer>
              </>
            ) : (
              <>
                <UPILoader
                  key={`${upiApp}`}
                  // @ts-ignore
                  onClickPay={createOrderForSubscription}
                />
              </>
            )}
          </>
        )}
      </>
    ) : (
      <CheckoutStateSelectWrapper>
        <CheckoutStateSelect
          states={states}
          setIsStateSelected={setIsStateSelected}
        />
      </CheckoutStateSelectWrapper>
    );

  return (
    <>
      {isMobile ? (
        <>
          {!paymentPossible ? (
            <>
              <MobileWrapper>{renderPaymentFlow()}</MobileWrapper>
            </>
          ) : (
            <>
              {upiApp === undefined || upiApp.length === 0 ? (
                <>
                  <MobileWrapper>{renderPaymentFlow()}</MobileWrapper>
                </>
              ) : (
                <>
                  <UPILoader
                    key={`${upiApp}`}
                    // @ts-ignore
                    onClickPay={createOrderForSubscription}
                  />
                </>
              )}
            </>
          )}
        </>
      ) : (
        renderPaymentFlow()
      )}
    </>
  );
};

export default PaymentFlow;
